import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';

// Module-level cache: single Firestore subscription shared across all
// components so re-mounting never causes a duplicate read.
let cachedData = null;
let listeners = [];
let unsubscribeSnapshot = null;
let unsubscribeAuth = null;

function startSnapshotListener() {
  if (unsubscribeSnapshot) return;

  unsubscribeSnapshot = onSnapshot(
    doc(db, 'systemConfig', 'statusOptions'),
    (snap) => {
      cachedData = snap.exists() ? snap.data() : {};
      listeners.forEach((fn) => fn(cachedData));
    },
    (err) => {
      console.error('useStatusConfig: Firestore error', err);
      unsubscribeSnapshot = null;
      cachedData = {};
      listeners.forEach((fn) => fn({}));
    }
  );
}

function subscribe(callback) {
  listeners.push(callback);

  if (!unsubscribeAuth) {
    // Gate the first query on Firebase Auth's own session-restore instead of
    // firing on mount: starting the listener before auth persistence finishes
    // loading can race ahead and get a spurious permission-denied that then
    // sticks empty for the rest of the tab session (no automatic retry).
    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        startSnapshotListener();
      } else if (cachedData === null) {
        cachedData = {};
        listeners.forEach((fn) => fn({}));
      }
    });
  } else if (cachedData !== null) {
    // Already have data — notify immediately
    callback(cachedData);
  }

  return () => {
    listeners = listeners.filter((fn) => fn !== callback);
    if (listeners.length === 0) {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
        unsubscribeSnapshot = null;
      }
      if (unsubscribeAuth) {
        unsubscribeAuth();
        unsubscribeAuth = null;
      }
      cachedData = null;
    }
  };
}

/**
 * Returns the systemConfig/statusOptions document from Firestore.
 * Subscribes once per app lifecycle — subsequent mounts reuse the cache.
 *
 * @returns {{ statusConfig: object|null, loading: boolean, error: Error|null }}
 */
export function useStatusConfig() {
  const [statusConfig, setStatusConfig] = useState(cachedData);
  const [loading, setLoading] = useState(cachedData === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = subscribe((data) => {
      setStatusConfig(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  return { statusConfig, loading, error };
}
